import {
  KeyboardEvent,
  RefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { PRICE_LADDER, complete } from '../../../../services/EpisodeScriptService';
import type { NamesTable } from '../../../../dorian-shared/types/episodeScript';
import type { AutocompleteSession } from '../types/basilTypes';

/** The keys that move the caret away from the words the list was built from (editor.js:796). */
const CARET_KEYS = new Set(['ArrowLeft', 'ArrowRight', 'Home', 'End', 'PageUp', 'PageDown']);

/** What the surface must give the autocomplete to work. */
export type UseAutocompleteOptions = {
  textareaRef: RefObject<HTMLTextAreaElement>;
  names: NamesTable | null;
  /** Every accepted item drives React state from the result, whichever way the text was written. */
  setText: (next: string) => void;
  /** Called after an accept so the caret context repaints. */
  onAfterAccept?: () => void;
  /**
   * The list never opens, and an accept never writes, while this is true.
   *
   * It covers a read-only script and the interval a confirmed batch is in flight (BAR-SHIP-14): an
   * accept is an editing entry point, so it must go inert with all the others rather than be the one
   * door left open into a revision the batch does not contain.
   */
  disabled?: boolean;
};

/** What the autocomplete hands the surface. */
export type UseAutocompleteResult = {
  session: AutocompleteSession | null;
  /** Rebuild the list from the caret. `forced` ignores an earlier Escape (Ctrl+Space). */
  refresh: (forced?: boolean) => void;
  close: () => void;
  move: (step: number) => void;
  accept: (index: number) => void;
  /** The one keydown handler on the textarea. It never intercepts a key while the list is closed. */
  handleKeyDown: (event: KeyboardEvent<HTMLTextAreaElement>) => void;
};

/**
 * Accept-to-insert only: no network, no engine id in any list, and nothing written unless the words
 * under the caret are still the words the list was built from (`complete`'s own contract,
 * editor.js:766-768).
 *
 * `document.execCommand('insertText')` is preferred so the writer's undo stack survives, and the
 * plain assignment is the fallback — but React state is driven from the result either way, so the
 * prototype's re-entry flag (which existed only because it edited the DOM behind React's back) is
 * deliberately not copied.
 */
export function useAutocomplete(options: UseAutocompleteOptions): UseAutocompleteResult {
  const {
    textareaRef,
    names,
    setText,
    onAfterAccept,
    disabled,
  } = options;
  const [session, setSession] = useState<AutocompleteSession | null>(null);
  const dismissedRef = useRef(false);
  const sessionRef = useRef<AutocompleteSession | null>(null);
  sessionRef.current = session;

  const close = useCallback(() => {
    sessionRef.current = null;
    setSession(null);
  }, []);

  const refresh = useCallback((forced = false) => {
    const node = textareaRef.current;
    if (!node || disabled) {
      close();

      return;
    }
    if (!forced && dismissedRef.current) {
      close();

      return;
    }
    if (document.activeElement !== node) {
      close();

      return;
    }
    if (node.selectionStart !== node.selectionEnd) {
      close();

      return;
    }
    const caret = node.selectionStart ?? 0;
    let result;
    try {
      result = complete(node.value, caret, { names: names ?? undefined, ladder: PRICE_LADDER });
    } catch {
      close();

      return;
    }
    // Never a list with nothing in it.
    if (!result || !result.items || result.items.length === 0) {
      close();

      return;
    }
    const next: AutocompleteSession = {
      anchor: result.anchor,
      prefix: result.prefix,
      items: result.items,
      caret,
      active: 0,
    };
    sessionRef.current = next;
    setSession(next);
  }, [close, disabled, names, textareaRef]);

  const move = useCallback((step: number) => {
    const open = sessionRef.current;
    if (!open) {
      return;
    }
    const count = open.items.length;
    const next = { ...open, active: (open.active + step + count) % count };
    sessionRef.current = next;
    setSession(next);
  }, []);

  // A list that was open when editing was locked is taken down with it, so nothing is left on the
  // surface that looks as though it would still write.
  useEffect(() => {
    if (disabled) {
      close();
    }
  }, [close, disabled]);

  const accept = useCallback((index: number) => {
    const node = textareaRef.current;
    const open = sessionRef.current;
    const item = open?.items[index];
    if (!node || !open || !item || disabled) {
      return;
    }
    const { value } = node;
    // The caret may have moved since the list was built. If the words under it are no longer the
    // words it was built from, the list is stale and nothing is written.
    if (value.slice(open.anchor, open.caret) !== open.prefix) {
      close();

      return;
    }
    close();
    node.focus({ preventScroll: true });
    node.setSelectionRange(open.anchor, open.caret);
    let typed = false;
    try {
      typed = document.execCommand('insertText', false, item.insert);
    } catch {
      typed = false;
    }
    const written = value.slice(0, open.anchor) + item.insert + value.slice(open.caret);
    if (!typed) {
      node.value = written;
    }
    const at = Math.min(open.anchor + item.caret, node.value.length);
    node.setSelectionRange(at, at);
    setText(node.value);
    dismissedRef.current = false;
    if (onAfterAccept) {
      onAfterAccept();
    }
    // Accepting closes the list. It opens again straight away only where the item deliberately left
    // the caret inside what it wrote: the character line, with its brackets waiting for a mood.
    if (item.caret < item.insert.length) {
      refresh(true);
    }
  }, [close, disabled, onAfterAccept, refresh, setText, textareaRef]);

  const handleKeyDown = useCallback((event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.ctrlKey && !event.altKey && !event.metaKey && (event.key === ' ' || event.code === 'Space')) {
      event.preventDefault();
      dismissedRef.current = false;
      refresh(true);

      return;
    }
    const open = sessionRef.current;
    if (!open) {
      // While the list is closed Tab moves focus and Enter makes a newline. Neither is intercepted.
      return;
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      dismissedRef.current = true;
      close();

      return;
    }
    if (event.altKey || event.metaKey || event.ctrlKey) {
      return;
    }
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      move(event.key === 'ArrowDown' ? 1 : -1);

      return;
    }
    if (event.key === 'Enter' || (event.key === 'Tab' && !event.shiftKey)) {
      event.preventDefault();
      accept(open.active);

      return;
    }
    if (CARET_KEYS.has(event.key) || event.key === 'Tab') {
      close();
    }
  }, [accept, close, move, refresh]);

  const clearDismissed = useCallback(() => {
    dismissedRef.current = false;
  }, []);

  return {
    session,
    refresh: useCallback((forced?: boolean) => {
      if (forced) {
        clearDismissed();
      }
      refresh(forced);
    }, [clearDismissed, refresh]),
    close,
    move,
    accept,
    handleKeyDown,
  };
}
